import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';

interface Slide {
  id: string;
  imageUrl: string;
  heading: string;
  subheading: string;
  content: string;
  buttonText: string;
  buttonLink: string;
  position: 'left' | 'center' | 'right';
  order: number;
  isPublished: boolean;
}

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const slidesCollectionRef = collection(db, 'heroSliders');
        const q = query(slidesCollectionRef, orderBy('order'));
        const querySnapshot = await getDocs(q);
        const fetchedSlides: Slide[] = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data() as Omit<Slide, 'id'>
          }))
          .filter(slide => slide.isPublished);
        console.log('Fetched slides:', fetchedSlides);
        setSlides(fetchedSlides);
      } catch (error) {
        console.error('Error fetching hero slides:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSlides();
  }, []);

  useEffect(() => {
    const nextSlide = () => {
      setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    };

    if (slides.length > 0) {
      const timer = setInterval(nextSlide, 7000);
      return () => clearInterval(timer);
    }
  }, [currentSlide, slides]);

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading slides...</div>; // Or a more sophisticated loader
  }

  if (slides.length === 0) {
    return <div className="flex items-center justify-center h-screen">No slides available.</div>;
  }

  return (
    <div className="relative w-full overflow-hidden bg-gradient-to-r from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700 h-[110vh] md:h-[110vh] lg:h-[110vh] -mt-[200px] md:-mt-[140px] lg:-mt-[220px]">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            currentSlide === index ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-[2s] hover:scale-105"
            style={{ backgroundImage: `url(${slide.imageUrl})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60 dark:from-black/70 dark:via-black/60 dark:to-black/80" />
          </div>

          <div className={`relative flex h-full items-center ${
            slide.position === 'right' ? 'justify-end' :
            slide.position === 'left' ? 'justify-start pl-8 lg:pl-16' : 'justify-center'
          }`}>
            <div className={`container mx-auto px-6 ${
              slide.position === 'center' ? 'flex justify-center' :
              slide.position === 'right' ? 'flex justify-end' : ''
            }`}>
              <div 
                className={`pt-16 md:pt-24 lg:pt-32 ${slide.position === 'right' ? 'text-right pr-8 lg:pr-16' :
                  slide.position === 'left' ? 'mr-auto text-left' : 'text-center'
                }`}
              >
                <span className={`mb-4 md:mb-6 inline-block rounded-full bg-blue-600/20 px-4 md:px-6 py-1.5 md:py-2 text-xs md:text-sm font-semibold tracking-wide text-blue-500 dark:text-blue-300 opacity-0 ${
                  slide.position === 'center' ? 'mx-auto' :
                  slide.position === 'right' ? 'ml-auto' : ''
                } ${currentSlide === index ? (
                  slide.position === 'left' ? 'animate-fadeInLeft animation-delay-200' :
                  slide.position === 'right' ? 'animate-fadeInRight animation-delay-200' :
                  'animate-fadeInUp animation-delay-200'
                ) : ''}`}>
                  {slide.subheading}
                </span>
                <h1 className={`mb-3 md:mb-4 font-serif text-3xl md:text-5xl lg:text-7xl font-bold leading-tight text-gray-900 dark:text-white opacity-0 ${
                  currentSlide === index ? (
                    slide.position === 'left' ? 'animate-fadeInLeft animation-delay-400' :
                    slide.position === 'right' ? 'animate-fadeInRight animation-delay-400' :
                    'animate-fadeInUp animation-delay-400'
                  ) : ''
                }`}>
                  {slide.heading}
                </h1>
                <p className={`mb-8 md:mb-10 text-base md:text-xl lg:text-2xl text-gray-700 dark:text-gray-300 opacity-0 leading-relaxed ${
                  slide.position === 'center' ? 'mx-auto max-w-2xl' :
                  slide.position === 'right' ? 'ml-auto max-w-2xl' : 'max-w-2xl'
                } ${currentSlide === index ? (
                  slide.position === 'left' ? 'animate-fadeInLeft animation-delay-600' :
                  slide.position === 'right' ? 'animate-fadeInRight animation-delay-600' :
                  'animate-fadeInUp animation-delay-600'
                ) : ''}`}>
                  {slide.content}
                </p>
                
                
                <div className={`opacity-0 ${
                  slide.position === 'center' ? 'text-center' :
                  slide.position === 'right' ? 'text-right' : ''
                } ${currentSlide === index ? (
                  slide.position === 'left' ? 'animate-fadeInLeft animation-delay-800' :
                  slide.position === 'right' ? 'animate-fadeInRight animation-delay-800' :
                  'animate-fadeInUp animation-delay-800'
                ) : ''}`}>
                  <Link
                    to={slide.buttonLink}
                    className="group inline-flex items-center rounded-full bg-blue-600 dark:bg-blue-500 px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-semibold text-white transition-all duration-300 hover:bg-blue-700 dark:hover:bg-blue-600 hover:shadow-xl hover:shadow-blue-600/40 dark:hover:shadow-blue-500/40 hover:scale-105"
                  >
                    {slide.buttonText}
                    <FaArrowRight className="ml-2 transition-transform duration-300 group-hover:translate-x-2" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))} 

      <div className="absolute bottom-8 md:bottom-12 left-1/2 z-20 flex -translate-x-1/2 space-x-3 md:space-x-4">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-2.5 md:h-3 rounded-full transition-all duration-500 transform hover:scale-125 touch-manipulation ${
              currentSlide === index
                ? 'w-10 md:w-12 bg-blue-500 dark:bg-blue-400 shadow-lg shadow-blue-500/50'
                : 'w-2.5 md:w-3 bg-white/40 hover:bg-white/60 dark:bg-white/30 dark:hover:bg-white/50'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroSlider;
